import {
  getLaunchDarklyRegion,
  getRegionKey,
  RegionContext,
  RegionProvider,
  toLaunchDarklyRegion,
  useGoogleAnalyticsSetup,
  usePendoSetup,
} from "@certn/utils";
import * as Sentry from "@sentry/react";
import {
  useFlags,
  useLDClient,
  withLDProvider,
} from "launchdarkly-react-client-sdk";
import React, { useEffect, useMemo } from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchPath,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { ChatBotWrapper } from "@certn/auth";

import { useApplicantAPI } from "@certn/api";

import constants from "../constants";
import { useUser } from "./hooks/api/useUser";
import { Providers } from "./providers";
import { BaseRoutes } from "./routes/BaseRoutes";
import { pendoMetadata } from "./utils";
import { AppErrorBoundary } from "./components/AppErrorBoundary";

import "./i18n";
import "./index.css";
import "./yup.config";

Sentry.init({
  dsn: `https://e0c281c820f661d13219a6938eaa7bd6@o132497.ingest.sentry.io/4506237024403456`,
  environment: constants.VITE_SENTRY_ENVIRONMENT,
  release: constants.VITE_SENTRY_RELEASE,
  enabled: !!constants.VITE_SENTRY_ENVIRONMENT,
  autoSessionTracking: true,
  ignoreErrors: [
    "ResizeObserver loop",
    "Non-Error exception captured with keys: _pendo",
    /^Non-Error promise rejection captured with value: Object Not Found Matching Id:.*?, MethodName:.*?, ParamCount:.*?/,
  ],
  integrations: [
    Sentry.thirdPartyErrorFilterIntegration({
      filterKeys: ["certn-applicant-portal"],
      behaviour: "apply-tag-if-exclusively-contains-third-party-frames",
    }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: 0.2,
  tracePropagationTargets: ["localhost", constants.DOMAIN],
  allowUrls: [/https:\/\/.+?\.certn\.co/],
});

if (constants.VITE_SENTRY_PULL_REQUEST) {
  Sentry.setTags({ pull_request: constants.VITE_SENTRY_PULL_REQUEST });
}

const App = () => {
  const { details: userDetails } = useUser();
  const userMetadata = useMemo(() => pendoMetadata(userDetails), [userDetails]);
  const ldClient = useLDClient();
  const {
    clientPortalAndApplicantPortalEnableAdaChatbot,
    clientPortalAndApplicantPortalEnableAdaChatbotInAus,
  } = useFlags();
  const region = getRegionKey();
  const ldRegion = toLaunchDarklyRegion(region);
  const location = useLocation();

  // Extract caseId from case routes to set LD organization context
  const caseMatch =
    matchPath("/apply/:caseId/*", location.pathname) ||
    matchPath("/report/:caseId", location.pathname);
  const caseId = caseMatch?.params?.caseId;

  const { data: caseData } = useApplicantAPI(
    caseId
      ? (`/api/internal/applicant/case/${caseId}/` as "/api/internal/applicant/case/{id}/")
      : null
  );

  useGoogleAnalyticsSetup({
    userId: userDetails?.auth0_id,
    measurementIds: constants.VITE_GA_MEASUREMENT_IDS?.split(" ") || [],
  });
  usePendoSetup(userMetadata);
  // useLanguage(); // TODO: Add language support

  useEffect(() => {
    if (userDetails?.auth0_id) {
      if (caseData?.client_group_account_auth0_id) {
        ldClient?.identify({
          kind: "multi",
          user: {
            kind: "user",
            key: userDetails.auth0_id,
            name: userDetails.auth0_email || "",
            is_consumer: true,
            region: ldRegion,
          },
          organization: {
            kind: "organization",
            key: caseData.client_group_account_auth0_id,
            name: caseData.client_group_name || "",
            is_consumer: true,
            region: ldRegion,
          },
        });
      } else {
        ldClient?.identify({
          kind: "user",
          key: userDetails?.auth0_id,
          name: userDetails?.auth0_email || "",
          is_consumer: true,
          region: ldRegion,
        });
      }
      const errorHandler = (error: Error) => {
        if (error?.name === "LaunchDarklyFlagFetchError") {
          // don't do anything here
          // https://support.launchdarkly.com/hc/en-us/articles/12998125691419-Error-LaunchDarklyFlagFetchError-network-error
          // "It is relatively common to see general network errors logged"
        } else {
          throw error;
        }
      };
      ldClient?.on("error", errorHandler);

      return () => {
        ldClient?.off("error", errorHandler);
      };
    }
  }, [
    ldClient,
    userDetails,
    caseData?.client_group_account_auth0_id,
    caseData?.client_group_name,
    ldRegion,
  ]);

  const isChatbotEnabledForRegion =
    region.toLowerCase() === "au"
      ? clientPortalAndApplicantPortalEnableAdaChatbotInAus
      : clientPortalAndApplicantPortalEnableAdaChatbot;

  return isChatbotEnabledForRegion ? (
    <ChatBotWrapper
      currentEnvironment={constants.MODE}
      currentAppName="applicant-portal"
      chatAlign="right"
    >
      <BaseRoutes />
    </ChatBotWrapper>
  ) : (
    <BaseRoutes />
  );
};

const AppWithLDProvider = constants.VITE_LAUNCH_DARKLY_KEY
  ? withLDProvider({
      clientSideID: constants.VITE_LAUNCH_DARKLY_KEY,
      context: {
        kind: "user",
        key: "anonymous-user-id",
        anonymous: true,
        is_consumer: true,
        region: getLaunchDarklyRegion(),
      },
    })(App)
  : App;

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <RegionProvider>
      <RegionContext.Consumer>
        {(region) => (
          <BrowserRouter basename={region}>
            <Providers>
              <AppErrorBoundary>
                <AppWithLDProvider />
              </AppErrorBoundary>
            </Providers>
          </BrowserRouter>
        )}
      </RegionContext.Consumer>
    </RegionProvider>
  </React.StrictMode>
);
